import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Pagination,
  Select
} from '@mui/material';
import { People as PeopleIcon, Delete as DeleteIcon, DriveFileMove as MoveIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import MarkdownRenderer from '../Common/MarkdownRenderer';

function QuestionList({
  questions,
  sortBy = 'questionNumber',
  showActions = true,
  onViewStudents,
  onDelete,
  isNotebookExample = false,
  getStatusIcon,
  onQuestionClick,
  pageSize = 10,
  questionStats = null, // 教师端：{ [questionId]: { attemptedCount, passedCount, passRate } }
  onDifficultyChange = null, // 教师端：(question, newDifficulty) => void，表格内直接改难度
  onMoveToSection = null, // 教师端笔记本例题：(question) => void，打开「移动到章节」对话框
}) {
  const showStatsColumn = questionStats && typeof questionStats === 'object';
  const getStat = (q) => {
    if (!showStatsColumn) return null;
    const raw = q.id ?? q._id;
    if (raw == null) return { attemptedCount: 0, passedCount: 0, passRate: 0, notPassedCount: 0, totalEnrolled: null, notSubmittedCount: 0 };
    const idStr = String(raw).replace(/^notebook_/, '');
    const stat =
      questionStats[String(raw)] ??
      questionStats[raw] ??
      (isNotebookExample ? questionStats['notebook_' + idStr] : null);
    return stat || { attemptedCount: 0, passedCount: 0, passRate: 0, notPassedCount: 0, totalEnrolled: null, notSubmittedCount: 0 };
  };
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const getSortedQuestions = () => {
    const sorted = [...questions];
    
    if (sortBy === 'difficulty') {
      const difficultyOrder = { easy: 1, medium: 2, hard: 3, exam: 4 };
      sorted.sort((a, b) => difficultyOrder[a.difficulty] - difficultyOrder[b.difficulty]);
    } else if (sortBy === 'title') {
      sorted.sort((a, b) => a.title.localeCompare(b.title));
    } else if (sortBy === 'status') {
      sorted.sort((a, b) => {
        const statusA = a.userStatus ? (a.userStatus.status === 'passed' ? 3 : 2) : 1;
        const statusB = b.userStatus ? (b.userStatus.status === 'passed' ? 3 : 2) : 1;
        return statusB - statusA;
      });
    } else {
      // Default: sort by questionNumber
      sorted.sort((a, b) => {
        // For notebook examples, use conceptBlockIndex, exampleIndex, and id for sorting
        if (a.isNotebookExample && b.isNotebookExample) {
          // First sort by conceptBlockIndex if available
          if (a.conceptBlockIndex != null && b.conceptBlockIndex != null) {
            if (a.conceptBlockIndex !== b.conceptBlockIndex) {
              return a.conceptBlockIndex - b.conceptBlockIndex;
            }
          }
          // Then sort by exampleIndex if available
          if (a.exampleIndex != null && b.exampleIndex != null) {
            if (a.exampleIndex !== b.exampleIndex) {
              return a.exampleIndex - b.exampleIndex;
            }
          }
          // Finally sort by id (numeric)
          // Handle both numeric IDs and string IDs (e.g., "notebook_541")
          const getIdNumber = (q) => {
            const id = q.id || q._id;
            if (typeof id === 'number') return id;
            if (typeof id === 'string') {
              // Extract numeric part from string like "notebook_541" or "541"
              const match = id.match(/(\d+)$/);
              return match ? parseInt(match[1]) : 0;
            }
            return 0;
          };
          const idA = getIdNumber(a);
          const idB = getIdNumber(b);
          return idA - idB;
        }
        // For regular questions, sort by questionNumber
        const numA = parseInt(a.questionNumber.replace(/\D/g, '')) || 0;
        const numB = parseInt(b.questionNumber.replace(/\D/g, '')) || 0;
        return numA - numB;
      });
    }
    
    return sorted;
  };

  const sortedQuestions = getSortedQuestions();
  
  // Calculate pagination
  const totalPages = Math.ceil(sortedQuestions.length / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedQuestions = useMemo(() => {
    return sortedQuestions.slice(startIndex, endIndex);
  }, [sortedQuestions, startIndex, endIndex]);

  // Reset to page 1 when questions change
  useEffect(() => {
    setPage(1);
  }, [questions.length]);

  const handlePageChange = (event, value) => {
    setPage(value);
    // Scroll to top of table
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleQuestionClick = (question) => {
    if (onQuestionClick) {
      const index = sortedQuestions.findIndex((q) => String(q.id ?? q._id) === String(question.id ?? question._id));
      onQuestionClick(question, { list: sortedQuestions, index: index >= 0 ? index : 0 });
    } else {
      if (question.isNotebookExample) {
        const exampleId = question.id.replace('notebook_', '');
        navigate(`/notebook-exercise/${exampleId}`);
      } else {
        navigate(`/question/${question._id || question.id}`);
      }
    }
  };

  if (sortedQuestions.length === 0) {
    return (
      <Card variant="outlined">
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            暂无题目
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>题号</strong></TableCell>
              {showStatsColumn ? (
                <TableCell sx={{ minWidth: 88 }}><strong>答题统计</strong></TableCell>
              ) : (
                <>
                  {!isNotebookExample && <TableCell><strong>类别</strong></TableCell>}
                  {isNotebookExample && <TableCell><strong>类型</strong></TableCell>}
                </>
              )}
              {!isNotebookExample && <TableCell><strong>题目类别</strong></TableCell>}
              {isNotebookExample && <TableCell><strong>标签</strong></TableCell>}
              {(!isNotebookExample || onDifficultyChange) && <TableCell align="center"><strong>难度</strong></TableCell>}
              {isNotebookExample && <TableCell><strong>标题</strong></TableCell>}
              {!isNotebookExample && <TableCell><strong>描述</strong></TableCell>}
              <TableCell align="center" sx={{ minWidth: 120 }}><strong>状态</strong></TableCell>
              {showActions && <TableCell align="center"><strong>操作</strong></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedQuestions.map((question) => (
              <TableRow
                key={question._id || question.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => handleQuestionClick(question)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {question.isNotebookExample ? (
                      <Chip 
                        label={`#${String(question.id || question._id).slice(-5)}`} 
                        size="small" 
                        variant="outlined" 
                      />
                    ) : (
                      <Chip label={question.questionNumber} size="small" variant="outlined" />
                    )}
                  </Box>
                </TableCell>
                {showStatsColumn ? (
                  <TableCell sx={{ minWidth: 88 }}>
                    {(() => {
                      const s = getStat(question);
                      const notSubmitted = s.totalEnrolled != null
                        ? (s.notSubmittedCount ?? (s.totalEnrolled - s.attemptedCount))
                        : null;
                      const notPassed = s.notPassedCount ?? (s.attemptedCount - s.passedCount);
                      return (
                        <Stack direction="column" spacing={0.25}>
                          <Typography variant="body2" color="success.main">
                            通过 {s.passedCount}
                          </Typography>
                          {s.totalEnrolled != null && (
                            <Typography variant="body2" color="warning.main">
                              未提交 {notSubmitted}
                            </Typography>
                          )}
                          <Typography variant="body2" color="error.main">
                            未通过 {notPassed}
                          </Typography>
                        </Stack>
                      );
                    })()}
                  </TableCell>
                ) : !isNotebookExample ? (
                  <TableCell>
                    <Chip label={question.category} color="primary" size="small" variant="outlined" />
                  </TableCell>
                ) : null}
                {/* 笔记本例题：与表头顺序一致 —— 类型 → 标签 */}
                {isNotebookExample && !showStatsColumn && (
                  <TableCell>
                    <Chip
                      label={question.questionType === 'multiple_choice' ? '选择题' :
                             question.questionType === 'short_answer' ? '简答题' :
                             question.questionType === 'proof' ? '证明题' :
                             question.questionType === 'code' ? '代码题' :
                             question.questionType === 'code_tracing' ? '代码追踪题' :
                             question.questionType || '未知类型'}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                )}
                {isNotebookExample && (
                  <TableCell sx={{ maxWidth: 220 }}>
                    {(() => {
                      const tags = question.conceptTags;
                      const arr = Array.isArray(tags) ? tags : (typeof tags === 'string' && tags ? (function () { try { return JSON.parse(tags); } catch (_) { return []; } })() : []);
                      if (!arr.length) return <Typography variant="body2" color="text.secondary">—</Typography>;
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {arr.slice(0, 4).map((t, i) => (
                            <Chip key={i} label={String(t)} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                          ))}
                          {arr.length > 4 && <Typography component="span" variant="caption" color="text.secondary">+{arr.length - 4}</Typography>}
                        </Box>
                      );
                    })()}
                  </TableCell>
                )}
                {!isNotebookExample && (
                  <>
                    <TableCell>
                      <Chip
                        label={question.questionType === 'multiple_choice' ? '选择题' :
                               question.questionType === 'short_answer' ? '简答题' :
                               question.questionType === 'proof' ? '证明题' :
                               question.questionType === 'code' ? '代码题' :
                               question.questionType === 'code_tracing' ? '代码追踪题' : 
                               question.questionType || '编程题'}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell align="center" onClick={(e) => onDifficultyChange && e.stopPropagation()}>
                      {onDifficultyChange ? (
                        <Select
                          value={question.difficulty || 'easy'}
                          onChange={(e) => {
                            e.stopPropagation();
                            onDifficultyChange(question, e.target.value);
                          }}
                          native
                          size="small"
                          sx={{
                            minWidth: 100,
                            fontSize: '0.8125rem',
                            height: 32,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            backgroundColor: 'background.paper',
                            '& .MuiNativeSelect-select': { py: 0.5, pr: 4 }
                          }}
                        >
                          <option value="easy">简单</option>
                          <option value="medium">中等</option>
                          <option value="hard">困难</option>
                          <option value="exam">考试原题</option>
                        </Select>
                      ) : (
                        <Chip
                          label={question.difficulty === 'easy' ? '简单' : question.difficulty === 'medium' ? '中等' : question.difficulty === 'exam' ? '考试原题' : '困难'}
                          size="small"
                          color={question.difficulty === 'easy' ? 'success' : question.difficulty === 'medium' ? 'warning' : question.difficulty === 'exam' ? 'info' : 'error'}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          maxWidth: 400,
                          maxHeight: 73,
                          overflow: 'hidden',
                          '& *': {
                            margin: 0,
                            padding: 0,
                          },
                          '& p': {
                            margin: 0,
                            padding: 0,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          },
                          '& .katex': {
                            fontSize: '0.9em',
                          },
                        }}
                      >
                        <MarkdownRenderer content={question.description || ''} />
                      </Box>
                    </TableCell>
                  </>
                )}
                {/* 笔记本例题的难度列：仅教师端（onDifficultyChange）显示，与学生端表头一致 */}
                {isNotebookExample && onDifficultyChange && (
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Select
                      value={question.difficulty || 'easy'}
                      onChange={(e) => {
                        e.stopPropagation();
                        onDifficultyChange(question, e.target.value);
                      }}
                      native
                      size="small"
                      sx={{
                        minWidth: 100,
                        fontSize: '0.8125rem',
                        height: 32,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        backgroundColor: 'background.paper',
                        '& .MuiNativeSelect-select': { py: 0.5, pr: 4 }
                      }}
                    >
                      <option value="easy">简单</option>
                      <option value="medium">中等</option>
                      <option value="hard">困难</option>
                      <option value="exam">考试原题</option>
                    </Select>
                  </TableCell>
                )}
                {isNotebookExample && (
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {(() => {
                        const rawTitle = question.title || '';
                        const difficultyLabels = ['简单', '中等', '困难', '考试原题'];
                        const isDifficultyLabel = difficultyLabels.includes(String(rawTitle).trim());
                        return (rawTitle && !isDifficultyLabel)
                          ? rawTitle
                          : (question.question ? String(question.question).replace(/<[^>]+>/g, '').trim().slice(0, 80) + (String(question.question).length > 80 ? '…' : '') : '—');
                      })()}
                    </Typography>
                  </TableCell>
                )}
                <TableCell align="center" sx={{ minWidth: 120 }}>
                  {question.userStatus?.status === 'passed' ? (
                    <Chip
                      label="已完成"
                      size="small"
                      color="success"
                      sx={{ fontWeight: 500 }}
                    />
                  ) : question.userStatus?.status === 'failed' ? (
                    <Chip 
                      label="错误" 
                      size="small" 
                      color="error"
                      sx={{ fontWeight: 500 }}
                    />
                  ) : question.userStatus ? (
                    <Chip 
                      label="已提交" 
                      size="small" 
                      color="warning"
                      sx={{ fontWeight: 500 }}
                    />
                  ) : (
                    <Chip
                      label="未提交"
                      size="small"
                      variant="outlined"
                      sx={{ 
                        color: 'text.secondary',
                        borderColor: 'text.secondary',
                        fontWeight: 500
                      }}
                    />
                  )}
                </TableCell>
                {showActions && (
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      {onViewStudents && (
                        <Tooltip title="查看学生提交">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={(e) => {
                              e.stopPropagation();
                              onViewStudents(e, question);
                            }}
                          >
                            <PeopleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {question.isNotebookExample && onMoveToSection && (
                        <Tooltip title="移动到章节">
                          <IconButton
                            size="small"
                            color="default"
                            onClick={(e) => {
                              e.stopPropagation();
                              onMoveToSection(question);
                            }}
                          >
                            <MoveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!question.isNotebookExample && onDelete && (
                        <Tooltip title="删除题目">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(question._id || question.id);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3, gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            共 {sortedQuestions.length} 题，第 {page} / {totalPages} 页
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Card>
  );
}

export default QuestionList;
